// "Active character" Zustand store + the pure transition function it
// drives. Models the player's currently-loaded save slot and the
// job-change mutation as defined by docs/03-jobs-skills.md §3.5
// (ADR 0004).
//
// Behavior summary (§3.5):
// - same target jobId         -> no-op
// - target jobId not in catalogue -> Error('unknown job id: …')
// - target job locked by JobUnlock -> Error('job locked: …')
// - target NOT in jobHistory  -> new job, jobLevel = 1
// - target IN jobHistory      -> revisit, jobLevel = max(1, floor(maxLv * ratio))
// - current jobId's maxLv is stored to jobHistory (max() with existing)
//
// The ratio defaults to 0.5. Returning-incentive scrolls (Common 0.6 …
// Mythic 1.0, §3.5) plumb through the same parameter — UI surface comes
// in Step 9 with the inventory.
//
// Phase 3 Step 8 (1/4).
// Phase 3 Step 9 (1/4): equipment / inventory mutation actions
// (equipItem / unequipItem / discardItem / seedSampleItems).
// Phase 3 Step 10a-1: dungeon-run actions
// (startDungeon / advanceDungeon / abandonDungeon).

import { create } from 'zustand';
import {
  JOBS_BY_ID,
  ITEM_BASES,
  generateItem,
} from '@renavora/content';
import {
  Rng,
  computeRunReward,
  eventSeed,
  pickBoon,
  pickTextEvent,
  pickTreasureEvent,
  rollOutcome,
  type DungeonBoonState,
  type DungeonRunState,
  type DungeonTemplate,
  type DungeonTemplateId,
  type Item,
  type ItemId,
  type NodeCoord,
  type PartyLink,
  type Slot,
} from '@renavora/shared';

import {
  saveCharacter as persistCharacter,
  writeLastActiveId,
  clearLastActiveId,
  type SerializedCharacter,
} from '../persistence/index.js';
import { applyDiscard, applyEquip, applyUnequip } from './inventory-actions.js';
import { dispatchEventOutcome } from './dungeon-event-dispatch.js';
import { MAX_SEED, applyAdvance, parseMapId, resolveMap, startDungeonRun } from './dungeon-run.js';
import { resolveDungeonTemplate } from './dungeon-template-resolver.js';
import { resolveNodeSubtype } from './dungeon-subtype.js';
import { generateRunLoot } from './dungeon-loot.js';
import { generateShopInventory } from './dungeon-shop.js';
import {
  isVictoryOutcome,
  resolveBattleResult,
  type BattleResolutionInfo,
} from './dungeon-resolution.js';
import { evaluateJobUnlock } from './job-unlock.js';
import {
  partnerToBorrowedAlly,
  refreshPartyLink as refreshPartyLinkInList,
  removePartyLinkById,
} from './party-link.js';
import {
  getLocalPartyMock,
  localSlotContext,
  localPartyMember,
} from './local-party-mock.js';
import {
  appendCompletedDungeon,
  applyRewardDeltaToCharacter,
  upsertJobHistoryMax,
  xpFloorForJobLevel,
} from './character-progression.js';

/**
 * Maximum simultaneous run-scope boons (ADR 0026 §2.6 / ADR 0030 §2.3.3).
 * Caller is expected to FIFO-drop index 0 when overflowing.
 */
const MAX_ACTIVE_BOONS = 3;

/**
 * Push a granted boon into `activeBoons` keeping at most MAX_ACTIVE_BOONS
 * entries (FIFO: drop the oldest when full). Pure helper so the rule is
 * unit-testable and the store callsite stays declarative.
 */
function applyBoonGrant(
  activeBoons: readonly DungeonBoonState[],
  granted: DungeonBoonState,
): readonly DungeonBoonState[] {
  if (activeBoons.length < MAX_ACTIVE_BOONS) {
    return [...activeBoons, granted];
  }
  // length === MAX_ACTIVE_BOONS (=3): drop the oldest, append the new.
  return [...activeBoons.slice(1), granted];
}

/**
 * Lookup the `DungeonTemplate` for a parsed mapId. Returns `null` instead
 * of throwing so callers (Step 10d-summary reward calc, boss loot drop)
 * can degrade gracefully when the template registry is stale.
 */
function templateOrNull(mapId: string): DungeonTemplate | null {
  try {
    const { templateId } = parseMapId(mapId);
    return resolveDungeonTemplate(templateId) ?? null;
  } catch {
    return null;
  }
}

/** Parse just the seed from a mapId; returns `null` on malformed input. */
function mapSeedFromId(mapId: string): number | null {
  try {
    return parseMapId(mapId).seed;
  } catch {
    return null;
  }
}

/**
 * Whether stepping onto `nodeType` should mark the dungeon node as
 * unresolved (locks the UI into the battle view until `recordBattleResult`
 * or `completeCurrentNode` is called).
 *
 * Step 10b-4 adds `'boss'` so the Floor 12 boss node routes into the
 * battle view as well. event/camp interactions land in 10c.
 */
function nodeRequiresResolution(nodeType: string): boolean {
  return nodeType === 'battle' || nodeType === 'elite' || nodeType === 'boss';
}

// Module-level lookup so equipItem doesn't re-scan ITEM_BASES on every
// call. The catalogue is static so the Map is safe to memoise at import
// time.
const ITEM_BASE_BY_ID = new Map(ITEM_BASES.map((b) => [b.id, b]));

/**
 * Catalogue-resolution predicate injected into `applyEquip`. Returns true
 * for two-handed weapons (spear / bow / staff / scythe — see ADR 0016).
 * Items whose baseId is not in the catalogue (test fixtures, future
 * content) are treated as one-handed.
 */
function isItemTwoHanded(item: Item): boolean {
  const base = ITEM_BASE_BY_ID.get(item.baseId);
  return base?.baseStats.kind === 'weapon' && base.baseStats.twoHanded === true;
}

export const DEFAULT_REVISIT_RATIO = 0.5;

/**
 * Dev-only sample-item generator defaults. Context targets low ilvl so
 * normal/magic/rare dominate; unique can roll occasionally. The default
 * seed is Date.now() so repeated presses produce fresh items — tests
 * that need reproducibility pass an explicit seed.
 *
 * TODO Phase 4: remove this together with the dev seed button when real
 * dungeon loot lands (Step 10+).
 */
export const DEV_SEED_DEFAULT_COUNT = 5;
const DEV_SEED_CTX = { dungeonTier: 1, difficultyIlvl: 10 } as const;

export type ActiveCharacterStatus = 'idle' | 'busy' | 'error';

export interface ActiveCharacterState {
  active: SerializedCharacter | null;
  status: ActiveCharacterStatus;
  error: string | null;
  activate: (record: SerializedCharacter) => void;
  unload: () => void;
  changeJob: (targetJobId: string, options?: { ratio?: number }) => Promise<SerializedCharacter>;
  equipItem: (item: Item) => Promise<SerializedCharacter>;
  unequipItem: (slot: Slot) => Promise<SerializedCharacter>;
  discardItem: (itemId: ItemId) => Promise<SerializedCharacter>;
  /**
   * Dev helper: generate `count` items deterministically and append them
   * to the inventory. Real loot pickup arrives with dungeons in Step 10.
   */
  seedSampleItems: (count?: number, seed?: number) => Promise<SerializedCharacter>;
  /**
   * Send a mutual-party request to a roster partner (local mock). On success the
   * cooperative synthetic partner accepts immediately and the formed link is
   * persisted onto `character.partyLinks`. Rejects (surfacing the formation
   * reason) when either side has no open slot or the pair is already linked.
   */
  sendPartyRequest: (partnerCharacterId: string) => Promise<SerializedCharacter>;
  /** Approve an inbound party request, forming and persisting the link. */
  approvePartyRequest: (requestId: string) => Promise<SerializedCharacter>;
  /** Decline an inbound party request. Leaves `character.partyLinks` untouched. */
  declinePartyRequest: (requestId: string) => Promise<SerializedCharacter>;
  /** Break an established party link, freeing both sides' slots. */
  breakPartyLink: (linkId: string) => Promise<SerializedCharacter>;
  /** Re-freeze the partner's latest snapshot into an existing link (same slot). */
  refreshPartyLink: (linkId: string) => Promise<SerializedCharacter>;
  /**
   * Replace `character.partyLinks` with the server's links (P6-5 online mode).
   * The server is the source of truth there; the persisted copy exists so
   * offline dungeon runs can keep borrowing partners.
   */
  adoptPartyLinks: (links: readonly PartyLink[]) => Promise<SerializedCharacter>;
  /**
   * Begin a new dungeon run on the given template. Rejects when a run is
   * already in progress so the caller has to explicitly `abandonDungeon`
   * first — avoids accidentally clobbering save progress.
   *
   * `seed` defaults to `Date.now() >>> 0` for a fresh random run; tests
   * pass an explicit seed for determinism. Out-of-range seeds throw.
   */
  startDungeon: (templateId: DungeonTemplateId, seed?: number) => Promise<SerializedCharacter>;
  /** Move the dungeon cursor to `target` if reachable (Step 10a-1). */
  advanceDungeon: (target: NodeCoord) => Promise<SerializedCharacter>;
  /** Drop the current run. No-op when none is in progress. */
  abandonDungeon: () => Promise<SerializedCharacter>;
  /**
   * Mark the current node as resolved (battle won, camp consumed). Called
   * by DungeonBattlePage's "Continue" button. No-op when no run is in
   * progress or when the node is already cleared.
   */
  completeCurrentNode: () => Promise<SerializedCharacter>;
  /**
   * Apply the battle resolution rules from docs/07 §7.5.1: update
   * partyHpRatio, consume a revive potion or accrue the −30% penalty
   * (cap −50%), and mark the node cleared. When the resolver signals an
   * auto-abandon (anomaly path), drop the run entirely.
   *
   * NOT idempotent — every call applies the resolver. Callers are
   * responsible for de-duplicating clicks (DungeonBattlePage hides the
   * Continue button after the click, so the UI cannot fire twice from
   * a single battle).
   *
   * Throws when no run is in progress; that's a programmer error from
   * the caller and must surface loudly.
   *
   * Phase 3 Step 10b-3.
   */
  recordBattleResult: (info: BattleResolutionInfo) => Promise<SerializedCharacter>;
  /**
   * Resolve the treasure event at `coord`: pick the catalogue entry +
   * roll one outcome deterministically against `eventSeed`, apply the
   * effects through `dispatchEventOutcome`, persist the resulting run
   * + character + inventory delta in a single write, and flip
   * `currentRunNodeCleared` to `true`.
   *
   * Throws when there is no active character, no current run, or when
   * `coord` does not refer to an event/treasure node in the active run.
   * The store treats the throw as a programmer error — DungeonsPage
   * only renders the treasure modal when the predicate holds.
   *
   * Phase 3 Step 10c-event-treasure.
   */
  resolveTreasureNode: (coord: NodeCoord) => Promise<SerializedCharacter>;
  /**
   * Resolve the text event at `coord` by picking choice `choiceIndex`:
   * load the catalogue entry via `pickTextEvent`, roll one outcome from
   * `choice.outcomes` deterministically, apply the effects through
   * `dispatchEventOutcome`, persist the resulting run + character + gold
   * + inventory delta in a single write, and flip
   * `currentRunNodeCleared` to true. Idempotent: a second call after
   * the node is cleared is a no-op (matches resolveTreasureNode).
   *
   * Throws when there is no active character, no current run, or when
   * `choiceIndex` is out of range for the rolled definition. The store
   * treats the throw as a programmer error — TextEventModal renders one
   * button per catalogue choice so `choiceIndex` should never overflow.
   *
   * Phase 3 Step 10c-event-text.
   */
  resolveTextChoice: (coord: NodeCoord, choiceIndex: number) => Promise<SerializedCharacter>;
  /**
   * Resolve the shop (merchant) node at `coord` by purchasing the offers
   * named in `offerIndices` (empty = leave without buying): regenerate the
   * deterministic stock via `generateShopInventory`, validate the indices
   * (unique + in range) and total cost against `character.gold`, debit the
   * gold, append the bought items to `character.inventory`, and flip
   * `currentRunNodeCleared` to true in a single write. Idempotent: a second
   * call after the node is cleared is a no-op (matches resolveTreasureNode),
   * so a double-click cannot double-charge.
   *
   * Throws when there is no active character, no current run, when an index
   * is duplicated or out of range, or when the total cost exceeds the
   * player's gold. The modal disables over-budget offers and the confirm
   * button, so a throw is a programmer/race guard — the store owns the
   * source-of-truth gate.
   *
   * Phase 3 Step 10c-shop.
   */
  resolveShopPurchase: (
    coord: NodeCoord,
    offerIndices: readonly number[],
  ) => Promise<SerializedCharacter>;
  reload: () => Promise<void>;
}

/**
 * Pure transition function — exported so unit tests and future flows
 * (e.g. dungeon-end revisit hook in Step 10+) can use it without
 * touching the store. No I/O, no time reads, no mutation of the input.
 *
 * Returns the input record unchanged when `targetJobId` equals the
 * current jobId (no-op). Throws `RangeError` only via the caller's
 * `Math.floor`/`Math.max` — we never throw from here ourselves; the
 * store layer does the `JOBS_BY_ID.has` validation.
 */
export function applyJobChange(
  active: SerializedCharacter,
  targetJobId: string,
  ratio: number = DEFAULT_REVISIT_RATIO,
): SerializedCharacter {
  if (active.character.jobId === targetJobId) {
    // Same job — no-op. Returning the *same* reference is important so
    // the store can detect and skip a redundant persist.
    return active;
  }

  const currentJobId = active.character.jobId;
  const currentJobLevel = active.character.jobLevel;

  // Update jobHistory for the *outgoing* job first: keep the max of any
  // previously-recorded maxLv and the level we're leaving at.
  const newHistory = upsertJobHistoryMax(
    active.character.jobHistory,
    currentJobId,
    currentJobLevel,
  );

  // Decide the incoming job's starting level. We look the target up in
  // the *updated* history so the rule reads off a consistent state.
  const targetVisited = newHistory.find((h) => h.jobId === targetJobId);
  const nextJobLevel = targetVisited ? Math.max(1, Math.floor(targetVisited.maxLv * ratio)) : 1;

  return {
    ...active,
    character: {
      ...active.character,
      jobId: targetJobId,
      jobLevel: nextJobLevel,
      jobHistory: newHistory,
      xp: xpFloorForJobLevel(nextJobLevel),
    },
  };
}

export const useActiveCharacterStore = create<ActiveCharacterState>((set, get) => ({
  active: null,
  status: 'idle',
  error: null,

  activate: (record) => {
    // Remember the slot so the next boot auto-restores it (App startup).
    // This is the single funnel for setting a *new* active id; persisting
    // mutations (changeJob / equip / persistDungeonRun) keep the same id so
    // they don't need to rewrite the pointer.
    writeLastActiveId(record.id);
    set({ active: record, status: 'idle', error: null });
  },

  unload: () => {
    clearLastActiveId();
    set({ active: null, status: 'idle', error: null });
  },

  changeJob: async (targetJobId, options = {}) => {
    const current = get().active;
    if (current === null) {
      // Up to the caller to guard the UI; the store still surfaces a
      // proper Error so misuse fails loudly during development.
      throw new Error('no active character');
    }

    const targetJob = JOBS_BY_ID.get(targetJobId);
    if (targetJob === undefined) {
      const msg = `unknown job id: ${targetJobId}`;
      set({ status: 'error', error: msg });
      throw new Error(msg);
    }

    const unlock = evaluateJobUnlock(current.character, targetJob);
    if (!unlock.unlocked) {
      const msg = `job locked: ${targetJobId} (${unlock.missing.join(', ')})`;
      set({ status: 'error', error: msg });
      throw new Error(msg);
    }

    const ratio = options.ratio ?? DEFAULT_REVISIT_RATIO;
    const next = applyJobChange(current, targetJobId, ratio);
    if (next === current) {
      // Same-job no-op — applyJobChange returns the same reference.
      // Clear any lingering error from a previous failure but don't
      // bother persisting.
      set({ status: 'idle', error: null });
      return current;
    }

    set({ status: 'busy', error: null });
    try {
      const persisted = await persistCharacter(next);
      set({ active: persisted, status: 'idle', error: null });
      return persisted;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // Critical: keep the *previous* active state on persist failure
      // so half-applied transitions don't leak into the UI.
      set({ status: 'error', error: msg });
      throw e;
    }
  },

  reload: async () => {
    const current = get().active;
    if (current === null) return;
    set({ status: 'busy', error: null });
    try {
      const { loadCharacter } = await import('../persistence/index.js');
      const refreshed = await loadCharacter(current.id);
      // If the record was deleted out from under us, drop the active and
      // forget the restore pointer so the next boot doesn't chase a ghost id.
      if (refreshed === undefined) clearLastActiveId();
      set({ active: refreshed ?? null, status: 'idle', error: null });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      set({ status: 'error', error: msg });
    }
  },

  equipItem: async (item) => {
    const current = get().active;
    if (current === null) throw new Error('no active character');
    let nextState;
    try {
      nextState = applyEquip(
        {
          equipment: current.character.equipment,
          inventory: current.character.inventory,
        },
        item,
        isItemTwoHanded,
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      set({ status: 'error', error: msg });
      throw e;
    }
    return persistMutation(set, current, nextState);
  },

  unequipItem: async (slot) => {
    const current = get().active;
    if (current === null) throw new Error('no active character');
    const nextState = applyUnequip(
      {
        equipment: current.character.equipment,
        inventory: current.character.inventory,
      },
      slot,
    );
    if (
      nextState.equipment === current.character.equipment &&
      nextState.inventory === current.character.inventory
    ) {
      // Empty slot — applyUnequip returned the same reference. Skip persist.
      set({ status: 'idle', error: null });
      return current;
    }
    return persistMutation(set, current, nextState);
  },

  discardItem: async (itemId) => {
    const current = get().active;
    if (current === null) throw new Error('no active character');
    const nextState = applyDiscard(
      {
        equipment: current.character.equipment,
        inventory: current.character.inventory,
      },
      itemId,
    );
    if (
      nextState.equipment === current.character.equipment &&
      nextState.inventory === current.character.inventory
    ) {
      // Not in inventory — applyDiscard returned the same reference.
      set({ status: 'idle', error: null });
      return current;
    }
    return persistMutation(set, current, nextState);
  },

  seedSampleItems: async (count, seed) => {
    const current = get().active;
    if (current === null) throw new Error('no active character');
    const rng = Rng.fromSeed(seed ?? Date.now() >>> 0);
    const n = count ?? DEV_SEED_DEFAULT_COUNT;
    const generated: Item[] = [];
    for (let i = 0; i < n; i++) {
      generated.push(generateItem(DEV_SEED_CTX, rng));
    }
    const nextState = {
      equipment: current.character.equipment,
      inventory: [...current.character.inventory, ...generated],
    };
    return persistMutation(set, current, nextState);
  },

  sendPartyRequest: async (partnerCharacterId) => {
    const current = get().active;
    if (current === null) throw new Error('no active character');
    let link;
    try {
      const result = getLocalPartyMock().sendRequest(
        localPartyMember(current),
        localSlotContext(current),
        partnerCharacterId,
      );
      link = result.link;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      set({ status: 'error', error: msg });
      throw e;
    }
    return persistPartyLinks(set, current, [...current.character.partyLinks, link]);
  },

  approvePartyRequest: async (requestId) => {
    const current = get().active;
    if (current === null) throw new Error('no active character');
    let link;
    try {
      const result = getLocalPartyMock().approveIncoming(requestId, localSlotContext(current));
      link = result.link;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      set({ status: 'error', error: msg });
      throw e;
    }
    // Persist first; only commit the queue resolution after the link is saved so
    // a save failure leaves the inbound request pending and re-approvable.
    const persisted = await persistPartyLinks(set, current, [...current.character.partyLinks, link]);
    getLocalPartyMock().commitApproval(requestId);
    return persisted;
  },

  declinePartyRequest: (requestId) => {
    // Declining touches only the mock's request queue, not the saved character,
    // so there is nothing to persist (kept Promise-returning for parity with the
    // other party actions and the future server-backed implementation).
    const current = get().active;
    if (current === null) return Promise.reject(new Error('no active character'));
    try {
      getLocalPartyMock().declineIncoming(requestId);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      set({ status: 'error', error: msg });
      return Promise.reject(e instanceof Error ? e : new Error(msg));
    }
    set({ status: 'idle', error: null });
    return Promise.resolve(current);
  },

  breakPartyLink: async (linkId) => {
    const current = get().active;
    if (current === null) throw new Error('no active character');
    const nextLinks = removePartyLinkById(current.character.partyLinks, linkId);
    if (nextLinks.length === current.character.partyLinks.length) {
      // No such link — leave the save untouched.
      set({ status: 'idle', error: null });
      return current;
    }
    // The mock holds no per-link state to free; the link lives on partyLinks.
    return persistPartyLinks(set, current, nextLinks);
  },

  refreshPartyLink: async (linkId) => {
    const current = get().active;
    if (current === null) throw new Error('no active character');
    const target = current.character.partyLinks.find((link) => link.linkId === linkId);
    if (target === undefined) {
      // No such link — leave the save untouched (resilient to a stale linkId).
      set({ status: 'idle', error: null });
      return current;
    }
    let nextLinks: PartyLink[];
    try {
      // Keyed by partner characterId (read off the persisted link), so refresh
      // works after a reload when the mock holds no per-link map.
      const freshPartner = getLocalPartyMock().refreshLink(target.partner.characterId);
      nextLinks = refreshPartyLinkInList(
        current.character.partyLinks,
        linkId,
        freshPartner,
        current.updatedAt,
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      set({ status: 'error', error: msg });
      throw e;
    }
    return persistPartyLinks(set, current, nextLinks);
  },

  adoptPartyLinks: async (links) => {
    const current = get().active;
    if (current === null) throw new Error('no active character');
    return persistPartyLinks(set, current, links);
  },

  startDungeon: async (templateId, seed) => {
    const current = get().active;
    if (current === null) throw new Error('no active character');
    if (current.character.currentRun !== null) {
      const msg = 'dungeon run already in progress';
      set({ status: 'error', error: msg });
      throw new Error(msg);
    }
    const resolvedSeed = seed ?? Date.now() >>> 0;
    if (!Number.isInteger(resolvedSeed) || resolvedSeed < 0 || resolvedSeed > MAX_SEED) {
      const msg = `invalid seed: ${String(seed)}`;
      set({ status: 'error', error: msg });
      throw new Error(msg);
    }
    if (resolveDungeonTemplate(templateId) === undefined) {
      const msg = `unknown dungeon template: ${templateId}`;
      set({ status: 'error', error: msg });
      throw new Error(msg);
    }
    // Mutual party links are the run-supply source (ADR 0038 §2.2). Convert
    // each partner's frozen snapshot into the in-battle ally representation;
    // startDungeonRun applies the per-run lock. `borrowedAt` rides the link's
    // own timestamp so the frozen run stays deterministic.
    const run = startDungeonRun(templateId, resolvedSeed, {
      borrowedAllies: current.character.partyLinks.map((link) =>
        partnerToBorrowedAlly(link.partner, link.linkedAt),
      ),
      runInstanceId: crypto.randomUUID(),
    });
    // Start node never carries combat — boot the run as "cleared" so the
    // UI shows the map immediately.
    return persistDungeonRun(set, current, run, true);
  },

  advanceDungeon: async (target) => {
    const current = get().active;
    if (current === null) throw new Error('no active character');
    const run = current.character.currentRun;
    if (run === null) {
      const msg = 'no dungeon run';
      set({ status: 'error', error: msg });
      throw new Error(msg);
    }
    // Re-resolve the map + template deterministically from the run's
    // mapId. Both are cheap (linear in the small node/edge count) and
    // avoid stashing the generated map / template on the run state.
    let map;
    let mapSeed: number;
    let template;
    try {
      const parsed = parseMapId(run.mapId);
      mapSeed = parsed.seed;
      map = resolveMap(parsed.templateId, parsed.seed, resolveDungeonTemplate);
      template = resolveDungeonTemplate(parsed.templateId);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      set({ status: 'error', error: msg });
      throw e;
    }
    let nextRun: DungeonRunState;
    try {
      nextRun = applyAdvance(run, map, target);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      set({ status: 'error', error: msg });
      throw e;
    }
    // Resolve the target node's type so we can flip the cleared flag the
    // moment the cursor lands. Battle / elite / boss nodes start
    // unresolved; camp / event lands pre-cleared and effects (boon /
    // rest / shop / text / treasure / ambush) run inline below.
    const row = map.floors[target[0] - 1];
    const targetNode = row?.[target[1]];
    let cleared = targetNode === undefined ? true : !nodeRequiresResolution(targetNode.type);

    // Step 10c camp effects: dispatch on submenu subtype.
    //   - boon (10c-boon): roll a boon → activeBoons (FIFO cap 3)
    //   - rest (10c-rest): partyHpRatio → 1.0 (status effects already
    //     clear at battle end per docs/07 §7.5.1, so nothing else to do)
    //   - shop (10c-shop): defer the cleared flag so DungeonsPage routes to
    //     ShopModal; `resolveShopPurchase` flips it back to true on leave.
    if (targetNode !== undefined && targetNode.type === 'camp' && template !== undefined) {
      const subtype = resolveNodeSubtype(template, targetNode, mapSeed);
      if (subtype === 'boon') {
        const granted = pickBoon({
          runSeed: mapSeed,
          coord: target,
          activeBoons: nextRun.activeBoons,
        });
        if (granted !== null) {
          nextRun = {
            ...nextRun,
            activeBoons: applyBoonGrant(nextRun.activeBoons, granted),
          };
        }
      } else if (subtype === 'rest') {
        // ADR 0026 §2.5: rest = HP 100% recovery + clear statuses. Statuses
        // already clear at battle end (docs/07 §7.5.1) so the dungeon-state
        // change is just `partyHpRatio = 1`. Skip the spread when already
        // full so we don't churn React state for a no-op.
        if (nextRun.partyHpRatio < 1) {
          nextRun = { ...nextRun, partyHpRatio: 1 };
        }
      } else if (subtype === 'shop') {
        // Merchant nodes need a UI resolution (browse + buy) before the
        // cursor can move on, so leave `cleared = false`. The shop has no
        // inline run-state change — `resolveShopPurchase` applies the gold /
        // inventory delta and clears the node when the player leaves.
        cleared = false;
      }
    }

    // Step 10c-event-treasure: defer the cleared flag for event nodes
    // that need a UI interaction. Only `treasure` is wired in this PR;
    // `text` and `ambush` stay walk-through (cleared) and will be
    // turned into UI flows in the next two PRs.
    if (targetNode !== undefined && targetNode.type === 'event' && template !== undefined) {
      const subtype = resolveNodeSubtype(template, targetNode, mapSeed);
      if (subtype === 'treasure' || subtype === 'text' || subtype === 'ambush') {
        // treasure / text → modal; ambush → DungeonBattlePage (Step
        // 10c-event-ambush). All three need a UI resolution before the
        // cursor can move on, so leave `cleared = false` for the routing
        // to pick up. The battle / modal flips it back to true via the
        // corresponding store action (`resolveTreasureNode`,
        // `resolveTextChoice`, or `recordBattleResult`).
        cleared = false;
      }
    }

    return persistDungeonRun(set, current, nextRun, cleared);
  },

  abandonDungeon: async () => {
    const current = get().active;
    if (current === null) throw new Error('no active character');
    if (current.character.currentRun === null) {
      // Nothing to abandon — keep state idle and don't touch the disk.
      set({ status: 'idle', error: null });
      return current;
    }
    return persistDungeonRun(set, current, null, null);
  },

  completeCurrentNode: async () => {
    const current = get().active;
    if (current === null) throw new Error('no active character');
    const run = current.character.currentRun;
    if (run === null) {
      // No run — nothing to complete; clear any prior error and idle.
      set({ status: 'idle', error: null });
      return current;
    }
    if (current.character.currentRunNodeCleared === true) {
      // Already cleared — idempotent.
      set({ status: 'idle', error: null });
      return current;
    }
    return persistDungeonRun(set, current, run, true);
  },

  recordBattleResult: async (info) => {
    const current = get().active;
    if (current === null) throw new Error('no active character');
    const run = current.character.currentRun;
    if (run === null) {
      const msg = 'no dungeon run';
      set({ status: 'error', error: msg });
      throw new Error(msg);
    }
    const resolved = resolveBattleResult(run, info);
    const nextRun = resolved.nextRun;
    const abandoned = resolved.abandoned;
    // - abandoned → run gone, cleared flag reset to null (same shape as
    //   abandonDungeon).
    // - boss defeat (10b-4) → keep currentRunNodeCleared at false so the
    //   player can re-fight the boss (or abandon explicitly). The boss
    //   battle store stays mounted on the next render.
    // - everything else (victory or non-boss defeat) → cleared = true so
    //   the UI returns to the map view (10b-3 default).
    let nextCleared: boolean | null;
    if (abandoned) {
      nextCleared = null;
    } else if (info.isBoss === true && !isVictoryOutcome(info.outcome)) {
      nextCleared = false;
    } else {
      nextCleared = true;
    }

    // Step 10d-summary: on boss victory, generate the run's loot drop and
    // push it onto the character's permanent inventory. The reward count
    // is a pure derivation of (template, run.penaltyPct) so the same run
    // always drops the same items; reload of the clear page is idempotent.
    // Non-boss battles and defeats do not drop loot here (Phase 4 will add
    // per-node mob drops).
    //
    // Step 10c-phase4-prep: also credit `RunReward.goldEarned` / `xpEarned`
    // to character.gold / character.xp here. The reward summary stays a
    // pure derivation, but the credit runs only once per boss win — the
    // caller (DungeonClearPage) cannot replay this branch without first
    // re-entering the run.
    let nextInventory: readonly Item[] | undefined;
    let rewardDelta: { gold: number; xp: number } | undefined;
    let completedDungeonId: DungeonTemplateId | undefined;
    if (!abandoned && info.isBoss === true && isVictoryOutcome(info.outcome) && nextRun !== null) {
      const template = templateOrNull(nextRun.mapId);
      if (template !== null) {
        const reward = computeRunReward(template, nextRun, {
          heroLevel: current.character.jobLevel,
        });
        const seed = mapSeedFromId(nextRun.mapId);
        if (seed !== null && reward.lootCount > 0) {
          const loot = generateRunLoot(template, seed, reward.lootCount);
          nextInventory = [...current.character.inventory, ...loot];
        }
        rewardDelta = { gold: reward.goldEarned, xp: reward.xpEarned };
        completedDungeonId = template.id;
      }
    }

    return persistDungeonRun(
      set,
      current,
      nextRun,
      nextCleared,
      nextInventory,
      rewardDelta,
      completedDungeonId,
    );
  },

  resolveTreasureNode: async (target) => {
    const current = get().active;
    if (current === null) throw new Error('no active character');
    const run = current.character.currentRun;
    if (run === null) {
      const msg = 'no dungeon run';
      set({ status: 'error', error: msg });
      throw new Error(msg);
    }
    // Idempotency guard: once the node is cleared (resolveTreasureNode
    // already ran for this entry), refuse to fire again. UI normally
    // disables the open button after the first click, but the store
    // owns the source-of-truth gate so a misbehaving caller cannot
    // double-credit gold / items by clicking faster than React renders.
    if (current.character.currentRunNodeCleared !== false) {
      return current;
    }

    let template: DungeonTemplate;
    let mapSeed: number;
    try {
      const parsed = parseMapId(run.mapId);
      mapSeed = parsed.seed;
      const resolved = resolveDungeonTemplate(parsed.templateId);
      if (resolved === undefined) {
        throw new Error(`unknown templateId: ${parsed.templateId}`);
      }
      template = resolved;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      set({ status: 'error', error: msg });
      throw e;
    }

    // Deterministic event roll. Same `(runSeed, coord)` always picks the
    // same definition + same outcome, so reloading the page between the
    // modal and the click reproduces the result (we do flip
    // `currentRunNodeCleared` so the modal won't re-fire post-resolve).
    const def = pickTreasureEvent({ runSeed: mapSeed, coord: target });
    const rng = Rng.fromSeed(eventSeed(mapSeed, target));
    // `pickTreasureEvent` consumed exactly one `nextU32` from the same
    // seed; advance our Rng past that draw so the outcome roll uses
    // independent bits and grant-item / grant-boon sub-rolls stay
    // statistically separated from the pick.
    rng.nextU32();
    const outcome = rollOutcome(def.outcomes, rng);

    const dispatch = dispatchEventOutcome(outcome, {
      run,
      template,
      runSeed: mapSeed,
      coord: target,
      rng,
    });

    const nextRun = dispatch.run;
    const nextInventory =
      dispatch.gainedItems.length > 0
        ? [...current.character.inventory, ...dispatch.gainedItems]
        : undefined;
    const rewardDelta = dispatch.goldDelta !== 0 ? { gold: dispatch.goldDelta, xp: 0 } : undefined;

    return persistDungeonRun(set, current, nextRun, true, nextInventory, rewardDelta);
  },

  resolveTextChoice: async (target, choiceIndex) => {
    const current = get().active;
    if (current === null) throw new Error('no active character');
    const run = current.character.currentRun;
    if (run === null) {
      const msg = 'no dungeon run';
      set({ status: 'error', error: msg });
      throw new Error(msg);
    }
    // Same idempotency guard as resolveTreasureNode: once cleared, no-op.
    // The modal also disables choice buttons after the first click but
    // the store owns the source-of-truth gate.
    if (current.character.currentRunNodeCleared !== false) {
      return current;
    }

    let template: DungeonTemplate;
    let mapSeed: number;
    try {
      const parsed = parseMapId(run.mapId);
      mapSeed = parsed.seed;
      const resolved = resolveDungeonTemplate(parsed.templateId);
      if (resolved === undefined) {
        throw new Error(`unknown templateId: ${parsed.templateId}`);
      }
      template = resolved;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      set({ status: 'error', error: msg });
      throw e;
    }

    const def = pickTextEvent({ runSeed: mapSeed, coord: target });
    if (choiceIndex < 0 || choiceIndex >= def.choices.length) {
      throw new Error(
        `resolveTextChoice: choiceIndex ${choiceIndex} out of range for ${def.id} (${def.choices.length} choices)`,
      );
    }
    const choice = def.choices[choiceIndex]!;
    // Outcome roll: derive a Rng from eventSeed and burn the `pickTextEvent`
    // draw + one extra u32 per choice index. This separates the
    // outcome-roll stream from the pick stream AND keeps every choice's
    // outcome statistically independent — i.e. picking choice 0 vs
    // choice 1 from the same node rolls against different bits.
    const rng = Rng.fromSeed(eventSeed(mapSeed, target));
    rng.nextU32(); // burn pickTextEvent's draw
    for (let i = 0; i < choiceIndex; i++) rng.nextU32(); // separate streams
    const outcome = rollOutcome(choice.outcomes, rng);

    const dispatch = dispatchEventOutcome(outcome, {
      run,
      template,
      runSeed: mapSeed,
      coord: target,
      rng,
    });

    const nextRun = dispatch.run;
    const nextInventory =
      dispatch.gainedItems.length > 0
        ? [...current.character.inventory, ...dispatch.gainedItems]
        : undefined;
    const rewardDelta = dispatch.goldDelta !== 0 ? { gold: dispatch.goldDelta, xp: 0 } : undefined;

    return persistDungeonRun(set, current, nextRun, true, nextInventory, rewardDelta);
  },

  resolveShopPurchase: async (target, offerIndices) => {
    const current = get().active;
    if (current === null) throw new Error('no active character');
    const run = current.character.currentRun;
    if (run === null) {
      const msg = 'no dungeon run';
      set({ status: 'error', error: msg });
      throw new Error(msg);
    }
    // Idempotency guard: once the node is cleared, refuse to fire again so a
    // double-click cannot re-debit gold / re-grant items (matches
    // resolveTreasureNode). The modal disables confirm after the first click
    // but the store owns the source-of-truth gate.
    if (current.character.currentRunNodeCleared !== false) {
      return current;
    }

    let template: DungeonTemplate;
    let mapSeed: number;
    try {
      const parsed = parseMapId(run.mapId);
      mapSeed = parsed.seed;
      const resolved = resolveDungeonTemplate(parsed.templateId);
      if (resolved === undefined) {
        throw new Error(`unknown templateId: ${parsed.templateId}`);
      }
      template = resolved;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      set({ status: 'error', error: msg });
      throw e;
    }

    // Regenerate the same deterministic stock the modal displayed and resolve
    // the selected offers from it. The store re-derives the inventory rather
    // than trusting a caller-supplied item payload, so the gold debit always
    // matches the displayed price and a stale client cannot inject items.
    const offers = generateShopInventory(template, mapSeed, target);
    const seen = new Set<number>();
    for (const i of offerIndices) {
      if (!Number.isInteger(i) || i < 0 || i >= offers.length) {
        throw new Error(
          `resolveShopPurchase: offerIndex ${i} out of range (${offers.length} offers)`,
        );
      }
      if (seen.has(i)) {
        throw new Error(`resolveShopPurchase: duplicate offerIndex ${i}`);
      }
      seen.add(i);
    }

    const purchased = offerIndices.map((i) => offers[i]!);
    const totalCost = purchased.reduce((sum, o) => sum + o.price, 0);
    if (totalCost > current.character.gold) {
      throw new Error(
        `resolveShopPurchase: insufficient gold (cost ${totalCost} > ${current.character.gold})`,
      );
    }

    const nextInventory =
      purchased.length > 0
        ? [...current.character.inventory, ...purchased.map((o) => o.item)]
        : undefined;
    const rewardDelta = totalCost > 0 ? { gold: -totalCost, xp: 0 } : undefined;

    // Shop entry leaves the run state untouched (no boon / HP change); only
    // gold + inventory move. Pass `run` through unchanged and flip
    // `currentRunNodeCleared` → true so the cursor can advance.
    return persistDungeonRun(set, current, run, true, nextInventory, rewardDelta);
  },
}));

type SetStateFn = (
  partial:
    | Partial<ActiveCharacterState>
    | ((s: ActiveCharacterState) => Partial<ActiveCharacterState>),
) => void;

/**
 * Shared persist tail used by every equipment/inventory mutation: merge the
 * new equipment/inventory into the active record, call saveCharacter, and
 * update the store on success. On failure keep the previous active state
 * untouched and surface the error.
 */
async function persistMutation(
  set: SetStateFn,
  current: SerializedCharacter,
  nextState: { equipment: ReadonlyArray<Item>; inventory: ReadonlyArray<Item> },
): Promise<SerializedCharacter> {
  const next: SerializedCharacter = {
    ...current,
    character: {
      ...current.character,
      equipment: nextState.equipment,
      inventory: nextState.inventory,
    },
  };
  set({ status: 'busy', error: null });
  try {
    const persisted = await persistCharacter(next);
    set({ active: persisted, status: 'idle', error: null });
    return persisted;
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    set({ status: 'error', error: msg });
    throw e;
  }
}

async function persistPartyLinks(
  set: SetStateFn,
  current: SerializedCharacter,
  partyLinks: readonly PartyLink[],
): Promise<SerializedCharacter> {
  const next: SerializedCharacter = {
    ...current,
    character: {
      ...current.character,
      partyLinks,
    },
  };
  set({ status: 'busy', error: null });
  try {
    const persisted = await persistCharacter(next);
    set({ active: persisted, status: 'idle', error: null });
    return persisted;
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    set({ status: 'error', error: msg });
    throw e;
  }
}

/**
 * Persist a dungeon-run update. Mirrors `persistMutation` for the
 * `currentRun` + `currentRunNodeCleared` pair; kept separate so the
 * equipment path doesn't grow optional run-state arguments. The
 * cleared flag is plumbed alongside the run because every transition
 * (start / advance / complete / abandon) sets it explicitly.
 */
async function persistDungeonRun(
  set: SetStateFn,
  current: SerializedCharacter,
  nextRun: DungeonRunState | null,
  nextCleared: boolean | null,
  /**
   * Optional inventory replacement. Step 10d-summary passes the boss-win
   * loot drop through here; other callsites omit it so character.inventory
   * stays untouched.
   */
  nextInventory?: readonly Item[],
  /**
   * Optional gold/xp deltas to credit alongside the run update. Step
   * 10c-phase4-prep boss-win branch passes `RunReward.goldEarned` /
   * `xpEarned` here so reward persistence rides the same write as the
   * loot drop. Omitted when the call site has no reward to apply.
   */
  rewardDelta?: { readonly gold: number; readonly xp: number },
  /** Optional first-clear id to append on boss victory. */
  completedDungeonId?: DungeonTemplateId,
): Promise<SerializedCharacter> {
  const nextCharacter =
    rewardDelta !== undefined
      ? applyRewardDeltaToCharacter(current.character, rewardDelta)
      : current.character;
  const completedDungeons =
    completedDungeonId === undefined
      ? nextCharacter.completedDungeons
      : appendCompletedDungeon(nextCharacter.completedDungeons, completedDungeonId);
  const next: SerializedCharacter = {
    ...current,
    character: {
      ...nextCharacter,
      currentRun: nextRun,
      currentRunNodeCleared: nextCleared,
      completedDungeons,
      ...(nextInventory !== undefined ? { inventory: nextInventory } : {}),
    },
  };
  set({ status: 'busy', error: null });
  try {
    const persisted = await persistCharacter(next);
    set({ active: persisted, status: 'idle', error: null });
    return persisted;
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    set({ status: 'error', error: msg });
    throw e;
  }
}
